import {
  finleyAssertUnreachable,
  typeguards as commonTypeguards,
} from '@finley-technologies/common';
import { DbTable } from '@finley-technologies/finley-types';
import {
  BbplGraph,
  typeguards as bbplTypeguards,
  progress,
  xlsx,
} from '@finley-technologies/tape-builder';

import {
  PickedCsvTape,
  PickedXlsxTape,
} from '../../../manualDataTapeUploads/ui/DataTapeFormatModal';
import { InstanceGraphState } from '../../types/dataPipelineTypes';

/** Gets derived details to show in the bbpl monitor sidebar drawer. */
export const getDrawerDetails = (
  bbplGraphState: InstanceGraphState,
  artifactId: string,
): {
  /** Errors associated with this graph node. */
  errors: progress.AggregatedError[];

  /** For data dictionary. */
  csvTape: PickedCsvTape | null;

  /** For data dictionary. */
  xlsxTape: PickedXlsxTape | null;
} => {
  const node = bbplGraphState.nodes.find((n) => n.artifact.id === artifactId);
  const artifact = node?.artifact;
  const instance = node?.instance;

  if (!artifact) {
    return { errors: [], csvTape: null, xlsxTape: null };
  }

  // Reconstruct a graph structure.
  const graph = {
    artifacts: bbplGraphState.nodes.map((n) => ({
      id: n.artifact.id,
      type: n.artifact.type,
      configuration: n.artifact.configuration,
      artifactSlug: n.artifact.externalDisplayName, // Workaround for now.
    })),
    dependencies: bbplGraphState.edges.map(({ source, target }) => ({
      inputArtifactId: source,
      outputArtifactId: target,
    })),
  };

  const rawArtifacts = BbplGraph.findDependenciesRecursively(graph, [
    artifact.id,
  ]).filter(
    ({ type }) =>
      type === 'rawLoanTape' ||
      type === 'rawSupplementalTape' ||
      type === 'rawRepaymentTape' ||
      type === 'rawCashBalanceFeed',
  );

  const dataDictionaryFieldGroups: xlsx.XlsxExtraction.FieldGroup[] = [];
  const errorFieldGroups: xlsx.XlsxExtraction.FieldGroup[] = [];
  rawArtifacts.forEach((rawArtifact) => {
    const fieldGroups = [
      ...xlsx.XlsxExtraction.getXlsxFieldGroups(graph, rawArtifact.id),
      ...xlsx.XlsxExtraction.getCsvFieldGroups(graph, rawArtifact.id),
    ];
    // Data dictionary is always associated with the raw node, because that's where the
    // upload button is shown.
    if (rawArtifact.id === artifactId) {
      dataDictionaryFieldGroups.push(...fieldGroups);
    }
    // Data errors will always be associated with either the sanitize or the xlsx2csv node.
    errorFieldGroups.push(
      ...fieldGroups.filter(
        (fieldGroup) =>
          fieldGroup.sanitizeArtifact.id === artifactId ||
          fieldGroup.xlsx2CsvArtifact?.id === artifactId,
      ),
    );
  });

  let csvTape: PickedCsvTape | null = null;
  let xlsxTape: PickedXlsxTape | null = null;
  dataDictionaryFieldGroups.forEach((fieldGroup) => {
    switch (fieldGroup.mode) {
      case 'csv':
        csvTape = { fileType: 'csv', columns: fieldGroup.columns };
        break;
      case 'extractTape':
      case 'extractValues':
        if (!xlsxTape) {
          xlsxTape = {
            fileType: 'xlsx',
            valueFieldGroups: [],
            tapeFieldGroups: [],
          };
        }
        if (fieldGroup.mode === 'extractValues') {
          xlsxTape.valueFieldGroups.push(fieldGroup);
        } else {
          xlsxTape.tapeFieldGroups.push(fieldGroup);
        }
        break;
      default:
        finleyAssertUnreachable(fieldGroup);
    }
  });

  if (instance?.status !== 'error') {
    return { errors: [], csvTape, xlsxTape };
  }

  const errors: progress.AggregatedError[] = [];
  instance.statusDetails.errors.forEach((error) => {
    switch (error.type) {
      case 'transformError':
        switch (error.content.type) {
          case 'columnValidation':
          case 'fieldValidation':
          case 'xlsx2Csv':
          case 'pyXlsx2Csv2':
            // These are all handled below as a special case.
            break;
          default:
            finleyAssertUnreachable(error.content);
        }
        break;
      case 'structuredPublicError':
        errors.push({ ...error.content, type: 'structuredPublicError' });
        break;
      case 'internalError':
        // Don't show internal errors.
        break;
      default:
        finleyAssertUnreachable(error);
    }
  });

  // Everything below is a special case for xlsx2csv and sanitize errors.
  if (
    !bbplTypeguards.isTapeBuilderSanitizeTask(artifact.configuration) &&
    !bbplTypeguards.isTapeBuilderXlsx2CsvTask(artifact.configuration)
  ) {
    return { errors, csvTape, xlsxTape };
  }

  const errorInstances: Pick<
    DbTable.ArtifactInstance.ErrorRecord,
    'artifactId' | 'status' | 'statusDetails'
  >[] = [];
  bbplGraphState.nodes.forEach((n) => {
    if (n.instance?.status === 'error') {
      errorInstances.push({
        artifactId: n.instance.artifactId,
        status: n.instance.status,
        statusDetails: n.instance.statusDetails,
      });
    }
  });

  const dataValidationErrors = errorFieldGroups
    .flatMap((fieldGroup) =>
      progress.toDataValidationError(fieldGroup, errorInstances),
    )
    .filter(commonTypeguards.isNotNullOrUndefined);

  errors.push(...dataValidationErrors);
  return { errors, csvTape, xlsxTape };
};
